(ns core.front.color
  (:require [clojure.math :as math]
            [clojure.set :as set]
            [clojure.string :as str]))

(def hex->int {\0 0
               \1 1
               \2 2
               \3 3
               \4 4
               \5 5
               \6 6
               \7 7
               \8 8
               \9 9
               \a 10
               \b 11
               \c 12
               \d 13
               \e 14
               \f 15})

(def int->hex (set/map-invert hex->int))

(defn destructure-html-color
  "Parses a #rrggbb hex color string into an [r g b] integer vector.
   Accepts both upper- and lowercase hex digits. Returns nil for invalid input."
  [hex]
  (when (and (string? hex) (re-matches #"#[0-9a-fA-F]{6}" hex))
    (let [normalized (str/lower-case hex)]
      (->> (subs normalized 1)
           (partition 2)
           (mapv (fn [[h l]]
                   (+ (* 16 (hex->int h))
                      (hex->int l))))))))

(defn render-hex [i]
  (let [capped (->> i
                    (min 255)
                    (max 0))]
    (str (int->hex (int (/ capped 16)))
         (int->hex (int (mod capped 16))))))

(defn render-html-color [vec]
  (->> vec (map render-hex) (apply str) (str "#")))

(defn vec-len [vec]
  (->> vec
       (map #(* %1 %1))
       (reduce +)
       math/sqrt))

(defn hsv->rgb
  "Given hue, saturation and value, returns HTML color code"
  ;; Based on https://www.rapidtables.com/convert/color/hsv-to-rgb.html
  [h s v]
  (let [c (* v s)
        x (* c (- 1 (abs (- (mod (/ h 60) 2) 1))))
        m (- v c)
        p (cond
            (< h 60) [c x 0]
            (< h 120) [x c 0]
            (< h 180) [0 c x]
            (< h 240) [0 x c]
            (< h 300) [x 0 c]
            :else [c 0 x])]
    (->> (map (comp (partial * 255)
                    (partial + m))
              p)
         render-html-color)))

(defn make-gray
  "Given an HTML color, returns a color that is closer to the middle of the RGB spectrum.
   Can be used as a hover-over color"
  [color]
  (let [col (destructure-html-color color)]
    (->> [30 30 30]
         (mapv #(* % (if (> (vec-len col) (* 127 (math/sqrt 3.0))) -1 1)))
         (mapv + col)
         render-html-color)))

;; WCAG 2.1 Contrast Ratio Calculation
;; https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html

(defn- srgb->linear
  "Convert sRGB component (0-255) to linear RGB value.
   Based on WCAG 2.1 relative luminance formula."
  [srgb]
  (let [s (/ srgb 255.0)]
    (if (<= s 0.04045)
      (/ s 12.92)
      (math/pow (/ (+ s 0.055) 1.055) 2.4))))

(defn relative-luminance
  "Calculate relative luminance of an RGB color.
   Returns value between 0 (black) and 1 (white).
   Formula: L = 0.2126 * R + 0.7152 * G + 0.0722 * B"
  [[r g b]]
  (+ (* 0.2126 (srgb->linear r))
     (* 0.7152 (srgb->linear g))
     (* 0.0722 (srgb->linear b))))

(defn contrast-ratio
  "Calculate contrast ratio between two colors.
   Colors can be hex strings (#rrggbb, upper- or lowercase) or RGB vectors [r g b].
   Returns ratio from 1:1 (identical) to 21:1 (black/white), or nil for invalid input.
   WCAG requirements:
   - AA normal text: 4.5:1
   - AA large text: 3:1
   - AAA normal text: 7:1
   - AAA large text: 4.5:1"
  [color1 color2]
  (let [rgb1 (if (string? color1) (destructure-html-color color1) color1)
        rgb2 (if (string? color2) (destructure-html-color color2) color2)]
    (when (and rgb1 rgb2)
      (let [l1      (relative-luminance rgb1)
            l2      (relative-luminance rgb2)
            lighter (max l1 l2)
            darker  (min l1 l2)]
        (/ (+ lighter 0.05)
           (+ darker 0.05))))))

;; WCAG 2.1 contrast thresholds.
;; Note: AAA large text also requires 4.5:1 — the same value as AA normal text.
;; wcag-level therefore cannot distinguish between those two cases.
(def wcag-aa-normal-text-threshold 4.5)
(def wcag-aa-large-text-threshold 3.0)
(def wcag-aaa-normal-text-threshold 7.0)

(defn wcag-level
  "Returns the WCAG compliance level for the given contrast ratio.
   Returns :aaa, :aa, :aa-large, or nil (fails all criteria)."
  [ratio]
  (cond
    (>= ratio wcag-aaa-normal-text-threshold) :aaa
    (>= ratio wcag-aa-normal-text-threshold) :aa
    (>= ratio wcag-aa-large-text-threshold) :aa-large
    :else nil))
